import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import FormRenderer from './FormRenderer';
import { formTemplateApi } from '../../services/formTemplates';
import { db } from '../../db';

export default function TemplatePreviewDialog({ open, onOpenChange, versionId, templateName, token }) {
  const [version, setVersion] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !versionId) {
      setVersion(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    formTemplateApi.getVersion(versionId, token)
      .catch(async err => {
        const status = err.response?.status;
        if (status === 401 || status === 403) {
          throw err;
        }
        const local = await db.form_template_versions.get(versionId);
        if (local) return local;
        throw err;
      })
      .then(res => {
        if (!cancelled) setVersion(res);
      })
      .catch(err => {
        if (!cancelled) {
          toast.error(err.response?.data?.detail || err.message || 'Failed to load version preview');
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [open, versionId, token]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Preview — {templateName || 'Template Version'}</DialogTitle>
          <DialogDescription>
            Read-only preview of published template version {version?.version_number ? `v${version.version_number}` : ''}.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <p className="text-slate-500 py-4">Loading preview…</p>
        ) : version?.schema ? (
          <div className="py-2 space-y-4">
            <FormRenderer schema={version.schema} answers={{}} errors={[]} readOnly onChange={() => {}} />
          </div>
        ) : (
          <p className="text-slate-500 py-4">No schema available for preview.</p>
        )}

        <div className="flex justify-end pt-2">
          <Button variant="outline" onClick={() => onOpenChange(false)}>Close</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

TemplatePreviewDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onOpenChange: PropTypes.func.isRequired,
  versionId: PropTypes.string,
  templateName: PropTypes.string,
  token: PropTypes.string.isRequired,
};
